import { type ReactNode, createContext, useContext } from 'react';
import {
  MutationTypes,
  type PairingMutation,
  type UseCartMutations,
} from '@hungryroot/cart';
import { useToast, Toast, ToastVariant } from '@hungryroot/components-toast';
import { type DietaryRestrictionsText } from '@hungryroot/dietary-restrictions';
import { type OrderIngredient } from '@hungryroot/orders';
import {
  type PairingV1 as Pairing,
  type Cart,
  type CartPairing,
  type PairingBundle,
} from '@hungryroot/rest';
import { useIngredientContainsNeverPeriodicity } from '../../../pages/spa/hooks/useIngredientContainsNeverPeriodicity';
import { useIngredientDetails } from '../../../pages/spa/hooks/useIngredientDetails';
import { usePairingDietaryIngredients } from '../../../pages/spa/hooks/usePairingDietaryIngredients';
import { getCartPairingBundle, getPairingCount } from '../../cart/query';
import {
  convertOrderIngredientToCartProduct,
  convertPairingToCartPairing,
} from '../../cart/transforms';
import { useCustomization } from '../../CustomizationProvider/CustomizationProvider';
import { RDPAddRemoveBtn } from '../../RDP/RDPAddRemoveButton/RDPAddRemoveButton';
import {
  useEditRecipe,
  type UseEditRecipeResult,
  type Params as UseEditRecipeParams,
  type EditRecipeOrderDetail,
  type PairingWithIngredientDetails,
} from '../hooks/useEditRecipe/useEditRecipe';
import { useEditRecipeMutations } from '../hooks/useEditRecipeMutations/useEditRecipeMutations';

import { IngredientEditorModal } from '../IngredientEditor/IngredientEditorModal/IngredientEditorModal';
import { getEffectiveDietaryIngredients } from './utils';
import styles from './PairingEdit.module.css';

export type Props = {
  /*
   * Children for the component. Can be a ReactNode or a render prop.
   * Render prop will be called with the "current" pairing
   */
  children:
    | ReactNode
    | ((data: {
        pairing: PairingWithIngredientDetails;
        dietaryIngredients: DietaryRestrictionsText[];
        ingredientContainsNeverPeriodicity: boolean;
        isLoading: boolean;
        editRecipeQuery: UseEditRecipeResult;
      }) => ReactNode);

  /*
   * The initial pairing. This is the pairing that the user selected
   * to initial the editing process.
   */
  initialPairing: Pairing;

  /*
   * Optional callback. Invoked when the user is "finished" editing.
   * This callback does not make any guarantees about when the edits were persisted.
   */
  onEditingDone?: () => void;

  /*
   * The id of the order where the edits will apply.
   */
  orderId?: UseEditRecipeParams['orderId'];

  /*
   * The initial bundle for editing. When initialBundle is passed, PairingEdit
   * will use that as a starting point instead of creating its own "virtual" bundle.
   */
  initialBundle?: {
    order_details: EditRecipeOrderDetail[];
    points: PairingBundle['points'];
  };
};

interface EditContextInterface {
  editRecipeQuery: UseEditRecipeResult;
  onEditingDone?: () => void;
  initialPairing: Pairing;
  initialBundle?: Props['initialBundle'];
  customization: {
    cart: Cart;
    mutations: UseCartMutations;
  } | null;
}

const EditingContext = createContext<EditContextInterface>(
  {} as EditContextInterface
);

export function PairingEdit(props: Props) {
  const { children, initialPairing, initialBundle, orderId, onEditingDone } =
    props;
  const customization = useCustomization();
  const editRecipeQuery = useEditRecipe({
    initialBundle,
    orderId,
    initialPairing,
  });

  const { pairing, bundle } = editRecipeQuery;

  const netIngredients =
    bundle.order_details.map((ingredient) => {
      return ingredient.product;
    }) || [];

  const ingredientContainsNeverPeriodicity =
    useIngredientContainsNeverPeriodicity(
      pairing.id,
      undefined,
      bundle.order_details.map(({ product }) => ({ id: product }))
    );

  const ingredientDetails = useIngredientDetails(pairing.id);
  const { data: dietaryIngredients, isLoading } = usePairingDietaryIngredients(
    pairing.id
  );

  const effectiveDietaryIngredients =
    netIngredients.length > 0
      ? getEffectiveDietaryIngredients(ingredientDetails, netIngredients)
      : dietaryIngredients;

  const pairingWithIngredientDetails = {
    ...pairing,
    ingredients: pairing.ingredients.map((ingredient) => {
      return {
        ...ingredient,
        dietaryIngredients:
          ingredientDetails?.[ingredient.id]?.dietaryIngredients,
        containsNeverItem:
          ingredientDetails?.[ingredient.id]?.containsNeverItem,
      };
    }),
  };

  return (
    <EditingContext.Provider
      value={{
        initialPairing,
        initialBundle,
        editRecipeQuery: {
          ...editRecipeQuery,
          pairing: pairingWithIngredientDetails,
        },
        onEditingDone,
        customization: 'cart' in customization ? customization : null,
      }}
    >
      {typeof children === 'function'
        ? children({
            pairing: pairingWithIngredientDetails,
            dietaryIngredients: effectiveDietaryIngredients,
            ingredientContainsNeverPeriodicity,
            isLoading,
            editRecipeQuery: {
              ...editRecipeQuery,
              pairing: pairingWithIngredientDetails,
            },
          })
        : children}
    </EditingContext.Provider>
  );
}

function sumPairingIngredientPoints(pairing: {
  ingredients: MinimumIngredient[];
}) {
  return pairing.ingredients.reduce(function (acc, obj) {
    const { points, amount } = obj;
    let newAcc = acc;

    for (let i = 0; i < amount; i += 1) {
      newAcc += points;
    }
    return newAcc;
  }, 0);
}

type MinimumIngredient = Pick<
  Pairing['ingredients'][number],
  'points' | 'amount' | 'id'
>;

type MinimumPairing = {
  id: Pairing['id'];
  points: Pairing['points'];
  ingredients: MinimumIngredient[];
};

function getIngredientById(
  pairing: MinimumPairing,
  ingredientId: number
): MinimumIngredient {
  const { ingredients } = pairing;
  const match = ingredients.find(({ id }) => {
    return id === ingredientId;
  });

  if (match === undefined) {
    throw new Error(`Unable to find ingredient with id "${ingredientId}"`);
  }

  return match;
}

// Returns the accumulated point difference
// of all modifications that have occured relative
// to the INITIAL points
//
// If an ingredient is removed, subtracts -POINTS_OF_REMOVED_INGREDIENT
// If an ingredient is added, adds POINTS_OF_REMOVED_INGREDIENT
//
// The loop starts from the END of the array of user modifications
// This has a few advantages:
// 1- A user can make any number of swaps. We don't keep all of those pairings
//    around. The last swap is guaranteed to be the "current" pairing
// 2- Starting from the last modification and working our way backwards means that we
//    can just immediately bail on the first swap, because the modifications before that
//    don't matter here
// 3- If the user swapped away and then BACK to the original pairing, we can just return
//    the modification points OR just revert back to the original points if there haven't been any modifications
function sumModificationPoints(
  initialPoints: number,
  initialPairing: MinimumPairing,
  pairing: MinimumPairing,
  modifications: PairingMutation[]
): number {
  let amount = 0;
  const { length: modificationsLength } = modifications;

  for (let i = modificationsLength - 1; i >= 0; i -= 1) {
    const modification = modifications[i];
    switch (modification.type) {
      case MutationTypes.RemoveIngredient: {
        const { ingredientId } = modification;
        const ingredient = getIngredientById(pairing, ingredientId);
        const {
          points: pairingIngredientPoints,
          amount: pairingIngredientQuantity,
        } = ingredient;

        amount -= pairingIngredientPoints * pairingIngredientQuantity;
        break;
      }
      case MutationTypes.AddIngredient: {
        const { ingredientId } = modification;
        const ingredient = getIngredientById(pairing, ingredientId);
        const {
          points: pairingIngredientPoints,
          amount: pairingIngredientQuantity,
        } = ingredient;

        amount += pairingIngredientPoints * pairingIngredientQuantity;
        break;
      }
      case MutationTypes.Swap: {
        const { pairingId } = modification;
        // swapped back
        if (pairingId === initialPairing.id) {
          return amount;
        }
        return pairing.points - initialPoints + amount;
      }
    }
  }
  return amount;
}

export function getDisplayPoints(params: {
  bundle?: Props['initialBundle'];
  pairing: MinimumPairing;
  initialPairing: MinimumPairing;
  modifications: PairingMutation[];
}): string {
  const { bundle, modifications, pairing, initialPairing } = params;
  const pairingPoints = sumPairingIngredientPoints(pairing);
  const pointsStartingPoint =
    bundle === undefined ? pairingPoints : parseFloat(bundle.points);
  const modificationPoints = sumModificationPoints(
    pointsStartingPoint,
    initialPairing,
    pairing,
    modifications
  );
  if (modificationPoints === 0) {
    return `${pointsStartingPoint}`;
  }

  if (bundle !== undefined) {
    return `${modificationPoints}`;
  }

  return `${pairingPoints + modificationPoints}`;
}

interface IngredientEditorModalProps {
  testid?: string;
  isOpen: boolean;
  onDismiss: () => void;
  isModalEditor?: boolean;
  onSubmitUpdate?: (params: OnSubmitUpdateParams) => void;
  contentClassName?: string;
  preventResetProducts?: boolean;
  isCustomModalEditor?: boolean;
  index?: number | null;
}

PairingEdit.IngredientEditorModal = (props: IngredientEditorModalProps) => {
  const editingContext = useContext(EditingContext);
  const { toast } = useToast();

  const {
    editRecipeQuery: { resetProductsToRemove, pairing },
    onEditingDone,
    customization,
  } = editingContext;

  if (customization === null) {
    return null;
  }

  const {
    testid,
    isOpen,
    onDismiss,
    isModalEditor,
    onSubmitUpdate,
    contentClassName,
    preventResetProducts,
    isCustomModalEditor,
    // the index here is for salad shooter tracking purposes; however, this has not been implemented
    // every place, so it's set as null by default to signal to the data team the data sent is not
    // part of the for you experience
    index = null,
  } = props;
  const { editRecipeQuery } = editingContext;
  const { ingredients: pairingIngredients } = pairing;

  function onSubmit(modifications: PairingMutation[]) {
    !preventResetProducts && resetProductsToRemove?.();
    if (modifications.length > 0) {
      toast(
        <Toast variant={ToastVariant.Success}>Recipe has been updated</Toast>
      );
    }

    if (onEditingDone === undefined) {
      return;
    }
    onEditingDone();
  }

  return (
    <IngredientEditorModal
      id={pairing.id}
      index={index}
      overlayClassName={`${styles['ingredients-editor']}`}
      contentClassName={`${contentClassName}`}
      editRecipeQuery={editRecipeQuery}
      testid={`${testid}-ingredients`}
      ingredients={pairingIngredients}
      isOpen={isOpen}
      onDismiss={onDismiss}
      isModalEditor={isModalEditor}
      isCustomModalEditor={isCustomModalEditor}
      onUpdate={(cart, modifications, updatedPairing) => {
        onSubmitUpdate &&
          onSubmitUpdate({
            cart,
            modifications,
            pairing: convertPairingToCartPairing(
              updatedPairing ? updatedPairing : (pairing as Pairing)
            ),
          });
        onSubmit(modifications);
      }}
    />
  );
};

function shouldShowToast(context: {
  editRecipeQuery: Pick<EditContextInterface['editRecipeQuery'], 'mutations'>;
  customization: EditContextInterface['customization'];
  initialPairing: EditContextInterface['initialPairing'];
}): boolean {
  const {
    customization,
    editRecipeQuery: { mutations },
    initialPairing,
  } = context;

  if (customization === null) {
    return false;
  }

  const { cart } = customization;
  const { id: initialPairingId } = initialPairing;

  if (getPairingCount(cart, initialPairingId) === 0) {
    return false;
  }

  const isSwap =
    mutations.find((mutation) => {
      return mutation.type === MutationTypes.Swap;
    }) !== undefined;
  const ingredientsModified = mutations.length > 0;
  return ingredientsModified === true || isSwap === true;
}

type OnSubmitUpdateParams = {
  modifications: PairingMutation[];
  pairing: CartPairing;
  cart: Cart;
};

export interface SubmitButtonProps {
  onSubmitAdd?: () => void;
  onSubmitUpdate?: (params: OnSubmitUpdateParams) => void;
  onPairingAdded?: () => void;
  onPairingRemoved?: () => void;
  dietaryIngredients?: DietaryRestrictionsText[];
  isLoading?: boolean;
}

PairingEdit.SubmitButton = (props: SubmitButtonProps) => {
  const editingContext = useContext(EditingContext);
  const { toast } = useToast();
  const {
    editRecipeQuery: { resetProductsToRemove, pairing, mutations },
    initialPairing,
    initialBundle,
    onEditingDone,
    customization,
  } = editingContext;
  const { createBundle, updateBundle } = useEditRecipeMutations();

  if (customization === null) {
    return null;
  }

  const showToast = shouldShowToast(editingContext);
  const {
    mutations: { addPairing },
    cart,
  } = customization;

  const {
    onPairingAdded,
    onPairingRemoved,
    onSubmitAdd = () => {
      if (mutations.length > 0) {
        createBundle({
          modifications: editingContext.editRecipeQuery.mutations,
          pairing: convertPairingToCartPairing(pairing as Pairing),
          pairingIngredients: pairing.ingredients.map((ingredient) => {
            return convertOrderIngredientToCartProduct(
              ingredient as OrderIngredient
            );
          }),
        });
        return;
      }

      addPairing({
        pairing: pairing as Pairing,
        dietaryProductIngredients: dietaryIngredients,
      });
    },
    onSubmitUpdate = async (params: OnSubmitUpdateParams) => {
      const { modifications } = params;
      const { id: bundleId } = getCartPairingBundle(cart, initialPairing.id);
      try {
        await updateBundle({
          bundleId,
          pairing: convertPairingToCartPairing(pairing as Pairing),
          modifications,
        });
      } catch {
        // TODO: Handle error
        void 0;
      }
    },
    dietaryIngredients,
    isLoading,
  } = props;

  function onSubmit() {
    resetProductsToRemove?.();
    if (showToast === true) {
      toast(
        <Toast variant={ToastVariant.Success}>Recipe has been updated</Toast>
      );
    }

    if (onEditingDone === undefined) {
      return;
    }
    onEditingDone();
  }

  const modified = mutations.length > 0;
  return (
    <div className={`rdp-customize ${styles.button}`}>
      <RDPAddRemoveBtn
        isLoading={isLoading}
        dietaryIngredients={dietaryIngredients}
        points={getDisplayPoints({
          bundle: initialBundle,
          pairing,
          modifications: mutations,
          initialPairing,
        })}
        pairing={pairing as Pairing}
        onUpdateClick={(cart) => {
          onSubmitUpdate({
            cart,
            modifications: editingContext.editRecipeQuery.mutations,
            pairing: convertPairingToCartPairing(pairing as Pairing),
          });
          onSubmit();
        }}
        onAddClick={() => {
          onSubmitAdd();
          onSubmit();
        }}
        onPairingAdded={onPairingAdded}
        onPairingRemoved={onPairingRemoved}
        closeModal={() => void 0}
        modified={modified}
      />
    </div>
  );
};
